import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Plus,
  Trash2,
  TrendingDown,
  TrendingUp,
  DollarSign,
  Search,
  CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import DateInput from '@/components/ui/date-input';
import { apiV1, tenantHeaders } from '@/lib/apiBase';
import { formatDate as formatDateDe, formatClocks } from '@/lib/utils';
import useListKeyboardNav, { LIST_ROW_CLASS } from '@/hooks/useListKeyboardNav';
import useTableSort from '@/hooks/useTableSort';
import { SortButton } from '@/components/table/SortableTh';

const FIXED_AMOUNT_COLUMNS = [
  { key: 'student_name', label: 'Schüler' },
  { key: 'course_name', label: 'Kurs' },
  { key: 'valid_from', label: 'Zeitraum' },
  { key: 'regular_amount', label: 'Regulär', right: true },
  { key: 'fixed_amount', label: 'Fixbetrag', right: true },
  { key: 'difference', label: 'Differenz', right: true },
  { key: 'reason', label: 'Grund' },
];

// Fehlende Beträge bleiben leer statt 0, damit sie hinten stehen und keine
// Nullzeile vor echten Werten auftaucht.
const amount = (value) => (value === null || value === undefined || value === '' ? null : Number(value));

const FIXED_AMOUNT_SORT_ACCESSORS = {
  student_name: (i) => i.student_name,
  course_name: (i) => i.course_name,
  valid_from: (i) => i.valid_from,
  regular_amount: (i) => amount(i.regular_amount),
  fixed_amount: (i) => amount(i.fixed_amount),
  difference: (i) => amount(i.difference),
  reason: (i) => i.reason,
};

const FixedAmountsPage = () => {
  const [fixedAmounts, setFixedAmounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [includeExpired, setIncludeExpired] = useState(false);
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [form, setForm] = useState({
    student_id: '',
    course_id: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: '',
    regular_amount: '',
    fixed_amount: '',
    reason: '',
    notes: ''
  });

  const authHeaders = () => tenantHeaders();

  useEffect(() => {
    const id = setTimeout(() => {
      fetchData();
    }, 250);
    return () => clearTimeout(id);
  }, [includeExpired, search]);

  useEffect(() => {
    fetchStudents();
    fetchCourses();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${apiV1()}/fixed-amounts/stats`, {
        credentials: 'include',
        headers: authHeaders()
      });
      const statsData = await statsRes.json();
      setStats(statsData);
      
      // Fetch fixed amounts
      const params = new URLSearchParams({
        include_expired: String(includeExpired),
        limit: '200',
      });
      if (search.trim()) params.set('q', search.trim());
      const url = `${apiV1()}/fixed-amounts?${params.toString()}`;
      const res = await fetch(url, {
        credentials: 'include',
        headers: authHeaders()
      });
      const data = await res.json();
      setFixedAmounts(data || []);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const all = [];
      for (let skip = 0; skip < 2000; skip += 100) {
        const res = await fetch(`${apiV1()}/students?limit=100&skip=${skip}`, {
          credentials: 'include',
          headers: authHeaders()
        });
        const data = await res.json();
        const page = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
        all.push(...page);
        if (page.length < 100) break;
      }
      all.sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
      setStudents(all);
    } catch (error) {
      console.error('Fehler beim Laden der Schüler:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const all = [];
      for (let skip = 0; skip < 1000; skip += 100) {
        const res = await fetch(`${apiV1()}/courses?limit=100&skip=${skip}`, {
          credentials: 'include',
          headers: authHeaders()
        });
        const data = await res.json();
        const page = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
        all.push(...page);
        if (page.length < 100) break;
      }
      setCourses(all);
    } catch (error) {
      console.error('Fehler beim Laden der Kurse:', error);
    }
  };

  const resetForm = () => {
    setForm({
      student_id: '',
      course_id: '',
      valid_from: new Date().toISOString().split('T')[0],
      valid_until: '',
      regular_amount: '',
      fixed_amount: '',
      reason: '',
      notes: ''
    });
  };

  const createFixedAmount = async () => {
    try {
      const payload = {
        ...form,
        course_id: form.course_id || null,
        valid_until: form.valid_until || null,
        regular_amount: form.regular_amount ? parseFloat(form.regular_amount) : null,
        fixed_amount: parseFloat(form.fixed_amount)
      };
      
      const res = await fetch(`${apiV1()}/fixed-amounts`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Fixbetrag erstellt');
        setShowCreateDialog(false);
        resetForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Erstellen');
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const updateFixedAmount = async () => {
    try {
      const payload = {
        valid_from: form.valid_from,
        valid_until: form.valid_until || null,
        regular_amount: form.regular_amount ? parseFloat(form.regular_amount) : null,
        fixed_amount: parseFloat(form.fixed_amount),
        reason: form.reason,
        notes: form.notes
      };
      
      const res = await fetch(`${apiV1()}/fixed-amounts/${selectedItem.id}`, {
        credentials: 'include',
        method: 'PUT',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Fixbetrag aktualisiert');
        setShowEditDialog(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Aktualisieren');
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const deleteFixedAmount = async (id) => {
    if (!window.confirm('Fixbetrag wirklich deaktivieren?')) return;
    
    try {
      const res = await fetch(`${apiV1()}/fixed-amounts/${id}`, {
        credentials: 'include',
        method: 'DELETE',
        headers: authHeaders()
      });
      
      if (res.ok) {
        toast.success('Fixbetrag deaktiviert');
        fetchData();
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const openEditDialog = (item) => {
    setSelectedItem(item);
    setForm({
      student_id: item.student_id,
      course_id: item.course_id || '',
      valid_from: item.valid_from,
      valid_until: item.valid_until || '',
      regular_amount: item.regular_amount?.toString() || '',
      fixed_amount: item.fixed_amount?.toString() || '',
      reason: item.reason || '',
      notes: ''
    });
    setShowEditDialog(true);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateDe(dateStr) || '-';
  };

  const { sort, toggleSort, sortRows } = useTableSort(FIXED_AMOUNT_SORT_ACCESSORS, { key: 'valid_from', dir: 'desc' });

  const filteredItems = sortRows(fixedAmounts);

  const { getRowProps } = useListKeyboardNav({
    items: filteredItems,
    onSelect: openEditDialog,
    enabled: !showEditDialog,
  });

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="fixed-amounts-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Fixbeträge</h1>
            <p className="text-gray-500">Individuelle Preisvereinbarungen pro Schüler/Kurs</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowCreateDialog(true);
            }}
            data-testid="new-fixed-amount-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neuer Fixbetrag
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Aktive Fixbeträge</p>
                    <p className="text-2xl font-bold">{stats.active}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingDown className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Ermäßigungen</p>
                    <p className="text-2xl font-bold text-green-600">{stats.discounts_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Zuschläge</p>
                    <p className="text-2xl font-bold text-orange-600">{stats.surcharges_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${stats.total_difference < 0 ? 'bg-green-100' : stats.total_difference > 0 ? 'bg-orange-100' : 'bg-gray-100'}`}>
                    <DollarSign className={`w-5 h-5 ${stats.total_difference < 0 ? 'text-green-600' : stats.total_difference > 0 ? 'text-orange-600' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Differenz gesamt</p>
                    <p className={`text-xl font-bold ${stats.total_difference < 0 ? 'text-green-600' : stats.total_difference > 0 ? 'text-orange-600' : ''}`}>
                      {formatCurrency(stats.total_difference)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Suche nach Schüler, Kurs oder Grund..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeExpired"
                  checked={includeExpired}
                  onChange={(e) => setIncludeExpired(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="includeExpired" className="text-sm cursor-pointer">
                  Abgelaufene anzeigen
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  {FIXED_AMOUNT_COLUMNS.map((col) => (
                    <TableHead key={col.key} className={col.right ? 'text-right' : undefined}>
                      <SortButton
                        sortKey={col.key}
                        sort={sort}
                        onSort={toggleSort}
                        testIdPrefix="fixed-amounts-sort"
                        reverse={col.right}
                      >
                        {col.label}
                      </SortButton>
                    </TableHead>
                  ))}
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ) : filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      Keine Fixbeträge gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item, idx) => (
                    <TableRow
                      key={item.id}
                      data-testid={`fixed-amount-${item.id}`}
                      className={LIST_ROW_CLASS}
                      {...getRowProps(idx, item)}
                    >
                      <TableCell className="font-medium">{item.student_name}</TableCell>
                      <TableCell>{item.course_name ? (formatClocks(item.course_name) || item.course_name) : <span className="text-gray-400">Alle Kurse</span>}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" />
                            {formatDate(item.valid_from)}
                          </div>
                          {item.valid_until && (
                            <div className="text-gray-500">bis {formatDate(item.valid_until)}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.regular_amount)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-medium">
                        {formatCurrency(item.fixed_amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.difference !== null && (
                          <Badge 
                            className={item.difference < 0 
                              ? 'bg-green-100 text-green-700' 
                              : item.difference > 0 
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-gray-100 text-gray-700'
                            }
                          >
                            {item.difference >= 0 ? '+' : ''}{formatCurrency(item.difference)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">{item.reason || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteFixedAmount(item.id)}
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Neuer Fixbetrag</DialogTitle>
              <DialogDescription>
                Individuelle Preisvereinbarung für einen Schüler
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Schüler *</Label>
                <select
                  className="w-full h-10 px-3 border rounded-md"
                  value={form.student_id}
                  onChange={(e) => setForm({...form, student_id: e.target.value})}
                >
                  <option value="">Schüler wählen...</option>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.last_name}, {s.first_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Kurs (optional)</Label>
                <select
                  className="w-full h-10 px-3 border rounded-md"
                  value={form.course_id}
                  onChange={(e) => setForm({...form, course_id: e.target.value})}
                >
                  <option value="">Alle Kurse (allgemein)</option>
                  {courses.map(c => (
                    <option key={c.id} value={c.id}>
                      {formatClocks(c.name) || c.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Leer lassen für eine allgemeine Preisanpassung
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Gültig ab *</Label>
                  <DateInput
                    
                    value={form.valid_from}
                    onChange={(e) => setForm({...form, valid_from: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Gültig bis</Label>
                  <DateInput
                    
                    value={form.valid_until}
                    onChange={(e) => setForm({...form, valid_until: e.target.value})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Leer = unbefristet</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Regulärer Betrag (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.regular_amount}
                    onChange={(e) => setForm({...form, regular_amount: e.target.value})}
                    placeholder="z.B. 65.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Nur zur Dokumentation</p>
                </div>
                <div>
                  <Label>Fixbetrag (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.fixed_amount}
                    onChange={(e) => setForm({...form, fixed_amount: e.target.value})}
                    placeholder="z.B. 50.00"
                  />
                </div>
              </div>
              
              <div>
                <Label>Grund</Label>
                <Input
                  value={form.reason}
                  onChange={(e) => setForm({...form, reason: e.target.value})}
                  placeholder="z.B. Geschwisterrabatt, Sozialermäßigung"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={createFixedAmount}
                disabled={!form.student_id || !form.fixed_amount || !form.valid_from}
              >
                Erstellen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Fixbetrag bearbeiten</DialogTitle>
              <DialogDescription>
                {selectedItem?.student_name} {selectedItem?.course_name && `- ${formatClocks(selectedItem.course_name) || selectedItem.course_name}`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Gültig ab *</Label>
                  <DateInput
                    
                    value={form.valid_from}
                    onChange={(e) => setForm({...form, valid_from: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Gültig bis</Label>
                  <DateInput
                    
                    value={form.valid_until}
                    onChange={(e) => setForm({...form, valid_until: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Regulärer Betrag (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.regular_amount}
                    onChange={(e) => setForm({...form, regular_amount: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Fixbetrag (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.fixed_amount}
                    onChange={(e) => setForm({...form, fixed_amount: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Grund</Label>
                <Input
                  value={form.reason}
                  onChange={(e) => setForm({...form, reason: e.target.value})}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={updateFixedAmount}>
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default FixedAmountsPage;
